import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  MessageSquare,
  Monitor,
  Globe,
  Edit3,
  ZapOff,
  ExternalLink,
} from "react-feather";
import { Button, Card, Col, Row } from "react-bootstrap";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import { getChannelsAPI } from "../../../api/api";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { companyUrl } from "../../../utils/mhNav";
import styles from "./ChannelOverview.module.scss";

// ── Channel icon map ──────────────────────────────────────────────────────────
function ChannelIcon({ key, size = 20 }) {
  if (key === "whatsapp") return <MessageSquare size={size} />;
  if (key === "web") return <Globe size={size} />;
  if (key === "google") return <Monitor size={size} />;
  return <Monitor size={size} />;
}

// ── Channel display name mapping ──────────────────────────────────────────────
function getChannelName(key) {
  const names = {
    google: "Google Meet",
    teams: "Microsoft Teams",
    whatsapp: "WhatsApp",
    web: "Web Chat",
    slack: "Slack",
  };
  return names[key] || key.charAt(0).toUpperCase() + key.slice(1);
}

// ── Welcome flow description mapping ──────────────────────────────────────────
function getWelcomeFlow(key) {
  const flows = {
    google: "Welcome message with calendar integration and meeting scheduling.",
    teams: "Welcome message with Teams presence and meeting booking.",
    whatsapp: "WhatsApp welcome flow with interactive buttons.",
    web: "Web chat welcome message with quick reply options.",
    slack: "Slack welcome message with slash commands.",
  };
  return flows[key] || "Welcome flow configured for this channel.";
}

// ── Status badge ──────────────────────────────────────────────────────────────
function StatusBadge({ enabled }) {
  if (enabled)
    return (
      <span className={`${styles.statusBadge} ${styles.badgeEnabled}`}>
        Enabled
      </span>
    );
  return (
    <span className={`${styles.statusBadge} ${styles.badgeDisabled}`}>
      Disabled
    </span>
  );
}

// ── Shared apex colours ───────────────────────────────────────────────────────
const CHART_COLORS = ["#644998", "#f73164"];

const BASE_APEX = {
  chart: { toolbar: { show: false }, fontFamily: "inherit" },
  grid: { borderColor: "rgba(82,82,108,0.12)", strokeDashArray: 4 },
  dataLabels: { enabled: false },
  tooltip: { theme: "light" },
};

export default function ChannelOverview() {
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [disableCard, setDisableCard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getChannelsAPI();
      // Handle both array response and {data: []} response
      const channelsData = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];
      setChannels(channelsData);
      setHasLoaded(true);
    } catch (e) {
      console.error("Error loading channels:", e);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Mock chart data (since API doesn't provide charts)
  const chartData = useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d.toLocaleDateString("en-US", { weekday: "short" });
    });

    return {
      messagesByChannel: last7Days.map(() => ({
        teams: Math.floor(Math.random() * 500) + 100,
        whatsapp: Math.floor(Math.random() * 300) + 50,
      })),
      completionRate: last7Days.map(() => ({
        day: "",
        pct: Math.floor(Math.random() * 30) + 65,
      })),
    };
  }, []);

  // ── Chart options ───────────────────────────────────────────────────────────
  const barOpts = useMemo(
    () => ({
      ...BASE_APEX,
      chart: { ...BASE_APEX.chart, stacked: false },
      colors: CHART_COLORS,
      xaxis: {
        categories: chartData.messagesByChannel.map((_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return d.toLocaleDateString("en-US", { weekday: "short" });
        }),
        labels: { style: { colors: "rgba(82,82,108,0.7)", fontSize: "11px" } },
      },
      yaxis: {
        labels: { style: { colors: "rgba(82,82,108,0.7)", fontSize: "11px" } },
      },
      plotOptions: { bar: { columnWidth: "52%", borderRadius: 4 } },
      legend: { position: "top", fontSize: "12px", markers: { radius: 4 } },
      fill: { opacity: 1 },
    }),
    [chartData],
  );

  const barSeries = useMemo(
    () => [
      {
        name: "Teams",
        data: chartData.messagesByChannel.map((x) => x.teams),
      },
      {
        name: "Google",
        data: chartData.messagesByChannel.map((x) => x.whatsapp),
      },
    ],
    [chartData],
  );

  const lineOpts = useMemo(
    () => ({
      ...BASE_APEX,
      colors: [CHART_COLORS[0]],
      xaxis: {
        categories: chartData.completionRate.map((_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return d.toLocaleDateString("en-US", { weekday: "short" });
        }),
        labels: { style: { colors: "rgba(82,82,108,0.7)", fontSize: "11px" } },
      },
      yaxis: {
        max: 100,
        labels: {
          formatter: (v) => `${v}%`,
          style: { colors: "rgba(82,82,108,0.7)", fontSize: "11px" },
        },
      },
      stroke: { curve: "smooth", width: 2.5 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.25,
          opacityTo: 0.02,
          stops: [0, 90, 100],
          colorStops: [
            { offset: 0, color: "#644998", opacity: 0.25 },
            { offset: 100, color: "#644998", opacity: 0 },
          ],
        },
      },
      markers: {
        size: 4,
        colors: ["#644998"],
        strokeColors: "#fff",
        strokeWidth: 2,
      },
    }),
    [chartData],
  );

  const lineSeries = useMemo(
    () => [
      {
        name: "Completion %",
        data: chartData.completionRate.map((x) => x.pct),
      },
    ],
    [chartData],
  );

  // ── Guards ──────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: "Company", path: companyUrl("dashboard") },
    { label: "Channels" },
  ];

  if (loading && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Channel overview" breadcrumbs={breadcrumbs} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !hasLoaded) {
    return (
      <div>
        <PageHeader title="Channel overview" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load"
          description={error.message || "Failed to load channels"}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  if (!loading && channels.length === 0) {
    return (
      <div>
        <PageHeader title="Channel overview" breadcrumbs={breadcrumbs} />
        <EmptyState
          icon={AlertTriangle}
          title="No channels"
          description="No communication channels configured yet."
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  // Generate mock messages per month based on channel
  const getMessagesThisMonth = (key) => {
    const messages = {
      google: 8,
      teams: 9,
      whatsapp: 6,
      web: 5670,
      slack: 8930,
    };
    return messages[key] || Math.floor(Math.random() * 20000) + 1000;
  };

  // Generate last template update date
  const getLastTemplateUpdate = () => {
    const daysAgo = Math.floor(Math.random() * 30);
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date;
  };

  // ── Main render ─────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Channel overview"
        breadcrumbs={breadcrumbs}
        secondaryActions={[
          { label: "Refresh", onClick: load, variant: "outline-secondary" },
        ]}
      />

      {/* ── Channel cards ── */}
      <Row className="g-3 mb-4">
        {channels.map((channel) => {
          const channelKey = channel.key;
          const isEnabled = channel.enabled;
          const channelName = getChannelName(channelKey);
          const welcomeFlow = getWelcomeFlow(channelKey);
          const messagesThisMonth = getMessagesThisMonth(channelKey);
          const lastTemplateUpdate = getLastTemplateUpdate();

          return (
            <Col md={6} key={channelKey}>
              <Card
                className={`
                  ${styles.cardChannel}
                  ${!isEnabled ? styles.cardDisabled : ""}
                `}
              >
                <Card.Body style={{ padding: "1.25rem 1.4rem 1.3rem" }}>
                  {/* Header */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <div className={styles.channelIcon}>
                      <ChannelIcon key={channelKey} size={18} />
                    </div>
                    <h5 className={styles.channelName}>{channelName}</h5>
                    <div className="ms-auto">
                      <StatusBadge enabled={isEnabled} />
                    </div>
                  </div>

                  {/* Welcome flow description */}
                  <p className={styles.welcomeFlow}>{welcomeFlow}</p>

                  {/* Meta strip */}
                  <div className={styles.metaRow}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Messages / month</span>
                      <span className={`${styles.metaValue} ${styles.accent}`}>
                        {messagesThisMonth.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>

      {/* ── Charts ── */}
      <Row className="g-3">
        <Col lg={6}>
          <Card className={styles.chartCard}>
            <Card.Body>
              <div className={styles.chartTitle}>Messages by channel (7d)</div>
              <Chart
                type="bar"
                height={290}
                options={barOpts}
                series={barSeries}
              />
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className={styles.chartCard}>
            <Card.Body>
              <div className={styles.chartTitle}>Completion rate (7d)</div>
              <Chart
                type="area"
                height={290}
                options={lineOpts}
                series={lineSeries}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ── Confirm modal ── */}
      <ConfirmModal
        isOpen={Boolean(disableCard)}
        onClose={() => setDisableCard(null)}
        onConfirm={() => {
          // Update local state to toggle enabled status
          setChannels((prevChannels) =>
            prevChannels.map((ch) =>
              ch.key === disableCard ? { ...ch, enabled: !ch.enabled } : ch,
            ),
          );
          const channel = channels.find((ch) => ch.key === disableCard);
          const newStatus = !channel?.enabled;
          toast.success(`Channel ${newStatus ? "enabled" : "disabled"} (mock)`);
          setDisableCard(null);
        }}
        title={
          channels.find((ch) => ch.key === disableCard)?.enabled
            ? "Disable channel?"
            : "Enable channel?"
        }
        message={
          channels.find((ch) => ch.key === disableCard)?.enabled
            ? "This will stop all messages on this channel. Provide a reason in a real deployment."
            : "This will re-enable all messages on this channel."
        }
        confirmLabel={
          channels.find((ch) => ch.key === disableCard)?.enabled
            ? "Disable channel"
            : "Enable channel"
        }
        danger={channels.find((ch) => ch.key === disableCard)?.enabled}
      />
    </div>
  );
}
